import { cn } from '@freelancer-os/ui/src/lib/utils';
import { Bot, CreditCard, FileText, Globe, LayoutGrid, LogOut, Settings, Users, Wallet, Zap } from 'lucide-react';
import { Link, Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@freelancer-os/ui/src/components/ui/dropdown-menu';
import { useAuthStore } from '../stores/authStore';
import { useLogout } from '../hooks/useAuth';
import { NudgeBanner } from '../pages/billing/components/NudgeBanner';

export function DashboardLayout() {
  const location = useLocation();
  const { t, i18n } = useTranslation('common');
  const user = useAuthStore((s) => s.user);
  const organization = useAuthStore((s) => s.organization);
  const logout = useLogout();

  const navigation = [
    { name: t('nav.dashboard'), href: '/', icon: LayoutGrid },
    { name: t('nav.aiChat'), href: '/chat', icon: Bot },
    { name: t('nav.invoices'), href: '/invoices', icon: FileText },
    { name: t('nav.bookkeeping'), href: '/bookkeeping', icon: Wallet },
    { name: t('nav.clients'), href: '/clients', icon: Users },
    { name: t('nav.billing'), href: '/billing', icon: CreditCard },
    { name: t('nav.settings'), href: '/settings', icon: Settings },
  ];

  // Get user initials for avatar
  const initials = user?.fullName
    ? user.fullName
        .split(' ')
        .map((w) => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '??';

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-surface-50">
      <NudgeBanner />
      <div className="flex flex-1 min-h-0">
      {/* Sidebar */}
      <aside className="w-[260px] border-r border-surface-200 bg-surface-50 flex flex-col shrink-0">
        {/* Brand */}
        <div className="px-5 py-5 border-b border-surface-200">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-brand-500 flex items-center justify-center">
              <Zap className="w-4.5 h-4.5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <span className="text-[15px] font-semibold tracking-tight text-surface-900">
                FreelancerOS
              </span>
              <span className="ml-1.5 text-[10px] font-bold bg-brand-500 text-white px-1.5 py-0.5 uppercase tracking-wider">
                PRO
              </span>
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="px-3 pt-4 space-y-0.5 flex-1">
          {navigation.map((item) => {
            const isActive =
              location.pathname === item.href ||
              (item.href !== '/' && location.pathname.startsWith(item.href));

            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 transition-colors',
                  isActive
                    ? 'bg-brand-500/6 border-l-2 border-brand-500'
                    : 'text-surface-500 hover:bg-surface-100',
                )}
              >
                <item.icon
                  className={cn(
                    'w-[18px] h-[18px]',
                    isActive ? 'text-brand-600 dark:!text-brand-400' : '',
                  )}
                  strokeWidth={1.8}
                />
                <span
                  className={cn(
                    'text-[13.5px]',
                    isActive
                      ? 'font-semibold text-brand-700 dark:!text-white'
                      : 'font-medium',
                  )}
                >
                  {item.name}
                </span>
              </Link>
            );
          })}
        </nav>

        {/* User Profile */}
        <div className="px-3 py-3 border-t border-surface-200">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 px-2 py-1.5 hover:bg-surface-100 transition-colors cursor-pointer rounded-md text-left">
                <div className="w-8 h-8 bg-gradient-to-br from-brand-400 to-emerald-500 flex items-center justify-center text-white text-[12px] font-bold shrink-0">
                  {initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-surface-800 truncate">
                    {user?.fullName || 'User'}
                  </div>
                  <div className="text-[11px] text-surface-400 truncate">
                    {organization?.name || ''}
                  </div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium text-surface-900">{user?.fullName}</p>
                <p className="text-xs text-surface-400 truncate">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/settings" className="cursor-pointer">
                  <Settings className="w-4 h-4 mr-2 text-surface-500" strokeWidth={1.8} />
                  {t('user.settings')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => i18n.changeLanguage(i18n.language === 'vi' ? 'en' : 'vi')}
                className="cursor-pointer"
              >
                <Globe className="w-4 h-4 mr-2 text-surface-500" strokeWidth={1.8} />
                {i18n.language === 'vi' ? 'English' : 'Tiếng Việt'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => logout.mutate()}
                className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-2" strokeWidth={1.8} />
                {t('user.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-auto">
        <Outlet />
      </main>
      </div>
    </div>
  );
}
